// Read-side AI hooks for any screen (content tools, editors…). The admin AI
// endpoints are admin-only: for an editor they answer 403, and these hooks then
// report "unknown" and let the server decide when the action is used.
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/shared/api/client";
import { aiApi } from "@/shared/api/endpoints";
import { qk } from "@/shared/api/queryKeys";
import type { AiFeature, AiSettings } from "@/shared/api/types";

const isForbidden = (e: unknown) => e instanceof ApiError && (e.status === 403 || e.status === 401);
const STALE_MS = 60_000;

export interface AiFeaturesState {
  /** Every AI feature with its effective config; empty when not allowed to read them. */
  features: AiFeature[];
  /** The signed-in staff member may not read the AI config (403). */
  forbidden: boolean;
  isLoading: boolean;
  /** Any other failure (never the 403). */
  error: unknown;
}

/** All AI features (admin endpoint). A 403 yields an empty list with `forbidden: true`, not an error. */
export function useAiFeatures(): AiFeaturesState {
  const q = useQuery({ queryKey: qk.ai.features, queryFn: aiApi.features, staleTime: STALE_MS });
  const forbidden = isForbidden(q.error);
  return { features: q.data?.items ?? [], forbidden, isLoading: q.isLoading, error: forbidden ? null : q.error };
}

/** The global AI settings (kill switch); `settings` is null while loading or when forbidden. */
export function useAiSettings(): { settings: AiSettings | null; forbidden: boolean; isLoading: boolean; error: unknown } {
  const q = useQuery({ queryKey: qk.ai.settings, queryFn: aiApi.settings, staleTime: STALE_MS });
  const forbidden = isForbidden(q.error);
  return { settings: q.data ?? null, forbidden, isLoading: q.isLoading, error: forbidden ? null : q.error };
}

export interface AiAvailability {
  /** Show the AI action as usable. True when unknown (403): the server decides. */
  available: boolean;
  /** False when the config could not be read (not an admin, or an error): `available` is then a guess. */
  known: boolean;
  isLoading: boolean;
  /** Why it is unavailable, in Vietnamese (for a tooltip); null when available. */
  reason: string | null;
}

/** Whether AI feature `key` can be used: it exists, it is enabled and so is the global kill switch. */
export function useAiAvailable(key: string): AiAvailability {
  const f = useAiFeatures();
  const s = useAiSettings();
  if (f.isLoading || s.isLoading) return { available: false, known: false, isLoading: true, reason: "Đang kiểm tra cấu hình AI…" };
  // Not allowed to read the config, or it failed: let the server answer (ai_disabled…) when used.
  if (f.forbidden || s.forbidden || f.error || s.error || !s.settings) {
    return { available: true, known: false, isLoading: false, reason: null };
  }
  const feature = f.features.find((x) => x.key === key);
  const reason = !s.settings.enabled ? "AI đang tắt trong Cài đặt AI."
    : !feature ? "Máy chủ chưa có tính năng AI này."
      : !feature.effective.enabled ? `Tính năng “${feature.title}” chưa được bật trong Tính năng AI.`
        : null;
  return { available: reason === null, known: true, isLoading: false, reason };
}
